/* istanbul ignore file */

import React, { forwardRef, memo, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { TextInput } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { useSelector } from '@xstate/react';

import { useLocationSearchFlags } from '../../hooks/useLocationSearchFlags';
import { useLocationSearchHelpers } from '../../hooks/useLocationSearchHelpers';
import { type LocationSearchService } from '../../machine';
import { LocationSearchField } from '../LocationSearchField';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchFieldForSearchType = memo(
  forwardRef<TextInput, LocationSearchFieldForSearchTypeProps>((props, ref) => {
    const { locationSearchActorRef } = props;

    const { formatMessage } = useIntl();

    const searchString = useSelector(
      locationSearchActorRef,
      (snapshot) => snapshot.context.searchString,
    );

    const deliverySearchString = useSelector(
      locationSearchActorRef,
      (snapshot) => snapshot.context.deliverySearchString,
    );

    const locationSearchType = useSelector(
      locationSearchActorRef,
      (snapshot) => snapshot.context.locationSearchType,
    );

    const { isLoadingLocations } = useLocationSearchFlags({ locationSearchActorRef });

    const { showRecentAndNearbyLocations, searchDeliveryAddresses } = useLocationSearchHelpers({
      locationSearchActorRef,
    });

    // ─── Helpers ─────────────────────────────────────────────────────────

    const onChangeText = useCallback(
      (enteredSearchString: string) => {
        locationSearchActorRef.send({
          type: 'SEARCH_FOR_PREDICTIONS',
          searchString: enteredSearchString,
        });
      },
      [locationSearchActorRef],
    );

    // ─── Delivery ────────────────────────────────────────────────────────

    if (locationSearchType === 'delivery') {
      return (
        <View style={styles.container}>
          <LocationSearchField
            ref={ref}
            style={styles.field}
            value={deliverySearchString}
            isDisabled={isLoadingLocations}
            onChangeText={searchDeliveryAddresses}
            placeholder={formatMessage(messages.locationDeliverySearchPlaceholder)}
          />
        </View>
      );
    }

    // ─── Pickup | Outpost ────────────────────────────────────────────────

    return (
      <View style={styles.container}>
        <LocationSearchField
          ref={ref}
          style={styles.field}
          value={searchString}
          isDisabled={isLoadingLocations}
          onChangeText={onChangeText}
          enterKeyHint="done"
          dataDetectorTypes="address"
          onClear={showRecentAndNearbyLocations}
          placeholder={formatMessage(messages.locationSearchPlaceholder)}
        />
      </View>
    );
  }),
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationDeliverySearchPlaceholder: {
    defaultMessage: 'Enter delivery address',
    description: 'Location | Delivery Search Placeholder',
  },
  locationSearchPlaceholder: {
    defaultMessage: 'Search store, city, state, or zip',
    description: 'Location | Search Placeholder',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  field: {
    minHeight: 42,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchFieldForSearchTypeProps = {
  locationSearchActorRef: LocationSearchService;
};
